/* eslint-disable max-lines-per-function */
import classNames from 'classnames'
import { useState } from 'react'
import ArrowDown from '@/assets/common/arrowDown.inline.svg'
import Link from '@/components/Common/Link'
import { StencilCDNImage } from '@/components/Common/StencilImage'
import { StockPeer } from '@/components/Stocks/Widgets/types'
import { formatNumberToCommaSeparatedString } from '@/root/utils/common/numbers/format-number-to-string'
import InStockL2Heading from '@/components/Stocks/Pages/InStockDetailPageV2/InL2Heading'
import Text from '../Common/NewText'

export const ShareHoldingTable = ({
  table,
  sliceLength = table.tableContent.length,
}: {
  table: {
    tableHeading?: Array<string | JSX.Element>
    tableContent: Array<{
      columns?: Array<JSX.Element | string | number>
      url?: string
      backgroundColor?: string
      name?: string
    }>
    innerBorder?: boolean
    withoutOuterBorder?: boolean
  }
  sliceLength?: number
}) => {
  const [toggle, setToggle] = useState(false)
  const clickToggle = () => setToggle(prevState => !prevState)
  return (
    <>
      <div className=" my-0  overflow-x-auto">
        <table
          className={classNames('my-0 w-full', {
            border: !table.withoutOuterBorder,
          })}
        >
          <thead>
            <tr className="bg-brand-grey-background">
              {table.tableHeading?.map((heading, index) => {
                return (
                  <th
                    className={classNames(
                      'text-13px text-brand-grey-base break-words p-2 text-left font-normal',
                      { 'border-r': table.innerBorder },
                      { 'text-right md:min-w-[130px]': index !== 0 },
                      {
                        'bg-brand-grey-background sticky left-0 min-w-[150px] md:min-w-[250px]':
                          index == 0,
                      },
                    )}
                    key={index}
                  >
                    {heading}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {table.tableContent
              .slice(0, toggle ? table.tableContent.length : sliceLength)
              .map((row, index) => {
                return (
                  <tr
                    key={index}
                    style={{ backgroundColor: row.backgroundColor }}
                    className={classNames({
                      'group cursor-pointer': row.url,
                    })}
                    // onClick added intentionally to make the row clickable
                    onClick={() => {
                      if (row.url) {
                        window.location.href = row.url
                      }
                    }}
                  >
                    {row.columns?.map((data, i) => {
                      return (
                        <td
                          className={classNames(
                            'break-words border-b px-2 py-3 text-sm',
                            { 'border-r': table.innerBorder },
                            {
                              'min-w-max text-right md:min-w-[130px]': i !== 0,
                            },
                            {
                              'sticky left-0 min-w-[150px] bg-white md:min-w-[250px]':
                                i == 0,
                            },
                          )}
                          key={i}
                          style={{ backgroundColor: row.backgroundColor }}
                        >
                          {data}
                          {/* {row.url ? (
                            <Link
                              href={row.url}
                              aTagClassNames="w-full block px-2 py-3"
                              title={row.name ?? ''}
                            >
                              {data}
                            </Link>
                          ) : (
                            data
                          )} */}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
          </tbody>
        </table>
      </div>

      {sliceLength < table.tableContent.length && (
        <div
          className="text-brand-blue-link my-2 flex cursor-pointer items-center justify-center text-center font-normal"
          onClick={clickToggle}
        >
          {toggle ? (
            <span className="flex items-center">
              See less{' '}
              <ArrowDown width={20} height={20} className="rotate-180" />
            </span>
          ) : (
            <span className="flex items-center">
              {' '}
              See all <ArrowDown width={20} height={20} />
            </span>
          )}
        </div>
      )}
    </>
  )
}
export default function PeerComparisonNew({
  data,
  ind_key,
  heading = 'Peer Comparison',
  short_name = '',
}: {
  data: StockPeer[]
  ind_key: string
  heading?: string
  short_name?: string
}) {
  const getTableHeading = mode => {
    if (mode === 'overview') {
      return [
        <div className="w-full" key={1}>
          Company
        </div>,
        'Analyst View',
        <div className="flex flex-col">
          Market Cap
          <Text appearance="overline" tag="span">
            (in ₹ Cr)
          </Text>
        </div>,
        '5 Year CAGR',
        'Debt to Asset Ratio',
        <div className="flex flex-col">
          Net Profit
          <Text appearance="overline" tag="span">
            (in ₹ Cr)
          </Text>
        </div>,
        <div className="flex flex-col">
          Yearly Revenue
          <Text appearance="overline" tag="span">
            (in ₹ Cr)
          </Text>
        </div>,
      ]
    } else if (mode === 'pe') {
      return [
        <div className="w-full" key={1}>
          Company
        </div>,
        ...(data[0].pe_ratios?.map(item => item.label ?? '-') ?? []),
      ]
    } else if (mode === 'returns') {
      return [
        <div className="w-full" key={1}>
          Company
        </div>,
        'Alpha',
        'Beta',
        ...(data[0].historic_returns?.map(
          item => `${item.label ?? '-'} Returns`,
        ) ?? []),
      ]
    } else if (mode === 'revenue') {
      return [
        <div className="w-full" key={1}>
          Company
        </div>,
        ...(data[0].annual_revenue?.map(item => (
          <div className="flex min-w-14 flex-col">
            {item.label ?? '-'}
            <Text appearance="overline" tag="span">
              (in ₹ Cr)
            </Text>
          </div>
        )) ?? []),
      ]
    } else if (mode === 'profitmargin') {
      return [
        <div className="w-full" key={1}>
          Company
        </div>,
        ...(data[0].pft_margin?.map(item => item.label ?? '-') ?? []),
      ]
    } else
      return [
        <div className="w-full" key={1}>
          Company
        </div>,
        'PE Ratio',
        'PB Ratio',
        'PEG Ratio',
        'EPS',
        'ROE',
        'ROCE',
        'Dividend Yield',
      ]
  }

  const getTableContent = mode => {
    if (mode === 'overview')
      return data.map(item => {
        return {
          columns: [
            <div
              className="group-hover:text-brand-blue-azureRadiance flex w-full items-center  gap-2"
              key={item.name}
            >
              <div className="flex-shrink-0">
                {item.logo && (
                  <StencilCDNImage
                    src={item.logo}
                    alt={`${item.name}`}
                    width={30}
                    height={30}
                    className="rounded-full"
                  />
                )}
              </div>
              {item.relativepath ? (
                <Link href={item.relativepath} title={item.name}>
                  {item.name}
                </Link>
              ) : (
                <span>{item.name}</span>
              )}
            </div>,
            item.sentiment ? (
              <span
                className={classNames({
                  'text-brand-red-cinnabar': item.sentiment === 'SELL',
                  'text-brand-green-dark': item.sentiment === 'BUY',
                  'text-brand-yellow-chileanFire': item.sentiment === 'HOLD',
                })}
                key={item.name}
              >
                {item.sentiment}
              </span>
            ) : (
              'NA'
            ),

            item.market_cap_value ? `${item.market_cap_value}` : 'NA',

            item['5y_cagr'] ? `${item['5y_cagr']}%` : 'NA',

            ...(item.sector === 'Banks'
              ? ['NA']
              : [
                  <span key={item.name} className="ml-10">
                    {(item.debt_to_asset_ratio ?? 0).toFixed(2)}
                  </span>,
                ]),
            item.net_profit
              ? `${formatNumberToCommaSeparatedString(item.net_profit, 0)}`
              : 'NA',
            item.yearly_revenue
              ? `${formatNumberToCommaSeparatedString(item.yearly_revenue, 0)}`
              : 'NA',
          ],
          url: item.relativepath ?? '',
          backgroundColor:
            item.ind_key === ind_key ? 'rgb(232,244,253)' : 'white',
          name: item.name ?? '',
        }
      })
    else if (mode === 'pe') {
      return data.map(item => {
        return {
          columns: [
            <div
              className="group-hover:text-brand-blue-azureRadiance flex w-full items-center  gap-2"
              key={item.name}
            >
              <div className="flex-shrink-0">
                {item.logo && (
                  <StencilCDNImage
                    src={item.logo}
                    alt={`${item.name}`}
                    width={30}
                    height={30}
                    className="rounded-full"
                  />
                )}
              </div>
              {item.relativepath ? (
                <Link href={item.relativepath} title={item.name}>
                  {item.name}
                </Link>
              ) : (
                <span>{item.name}</span>
              )}
            </div>,

            ...(item.pe_ratios?.map(item => (item.value ? item.value : 'NA')) ??
              []),
          ],
          url: item.relativepath ?? '',
          backgroundColor:
            item.ind_key === ind_key ? 'rgb(232,244,253)' : 'white',
          name: item.name ?? '',
        }
      })
    } else if (mode === 'revenue') {
      return data.map(item => {
        return {
          columns: [
            <div
              className="group-hover:text-brand-blue-azureRadiance flex w-full items-center  gap-2"
              key={item.name}
            >
              <div className="flex-shrink-0">
                {item.logo && (
                  <StencilCDNImage
                    src={item.logo}
                    alt={`${item.name}`}
                    width={30}
                    height={30}
                    className="rounded-full"
                  />
                )}
              </div>
              {item.relativepath ? (
                <Link href={item.relativepath} title={item.name}>
                  {item.name}
                </Link>
              ) : (
                <span>{item.name}</span>
              )}
            </div>,

            ...(item.annual_revenue?.map(item =>
              item.value
                ? `${formatNumberToCommaSeparatedString(item.value, 0)}`
                : 'NA',
            ) ?? []),
          ],
          url: item.relativepath ?? '',
          backgroundColor:
            item.ind_key === ind_key ? 'rgb(232,244,253)' : 'white',
          name: item.name,
        }
      })
    } else if (mode === 'profitmargin') {
      return data.map(item => {
        return {
          columns: [
            <div
              className="group-hover:text-brand-blue-azureRadiance flex w-full items-center  gap-2"
              key={item.name}
            >
              <div className="flex-shrink-0">
                {item.logo && (
                  <StencilCDNImage
                    src={item.logo}
                    alt={`${item.name}`}
                    width={30}
                    height={30}
                    className="rounded-full"
                  />
                )}
              </div>
              {item.relativepath ? (
                <Link href={item.relativepath} title={item.name}>
                  {item.name}
                </Link>
              ) : (
                <span>{item.name}</span>
              )}
            </div>,

            ...(item.pft_margin?.map(item =>
              item.value ? <span key={item.label}>{item.value}%</span> : 'NA',
            ) ?? []),
          ],
          url: item.relativepath ?? '',
          backgroundColor:
            item.ind_key === ind_key ? 'rgb(232,244,253)' : 'white',
          name: item.name,
        }
      })
    } else if (mode === 'returns') {
      return data.map(item => {
        return {
          columns: [
            <div
              className="group-hover:text-brand-blue-azureRadiance bg-brandt flex w-full  items-center gap-2"
              key={item.name}
            >
              <div className="flex-shrink-0">
                {item.logo && (
                  <StencilCDNImage
                    src={item.logo}
                    alt={`${item.name}`}
                    width={30}
                    height={30}
                    className="rounded-full"
                  />
                )}
              </div>
              {item.relativepath ? (
                <Link href={item.relativepath} title={item.name}>
                  {item.name}
                </Link>
              ) : (
                <span>{item.name}</span>
              )}
            </div>,
            item.alpha ? item.alpha.toFixed(2) : 'NA',
            item.beta ? item.beta.toFixed(2) : 'NA',
            ...(item.historic_returns?.map(item => {
              if (item.percentage) {
                return (
                  <span
                    key={item.label}
                    className={classNames({
                      'text-brand-red-cinnabar': item.percentage < 0,
                      'text-brand-green-dark': item.percentage >= 0,
                    })}
                  >
                    {item.percentage}%
                  </span>
                )
              } else {
                return 'NA'
              }
            }) ?? []),
          ],
          url: item.relativepath ?? '',
          backgroundColor:
            item.ind_key === ind_key ? 'rgb(232,244,253)' : 'white',
          name: item.name,
        }
      })
    } else {
      return data.map(item => {
        return {
          columns: [
            <div
              className="group-hover:text-brand-blue-azureRadiance flex w-full items-center gap-2"
              key={item.name}
            >
              <div className="flex-shrink-0">
                {item.logo && (
                  <StencilCDNImage
                    src={item.logo}
                    alt={`${item.name}`}
                    width={30}
                    height={30}
                    className="rounded-full"
                  />
                )}
              </div>
              {item.relativepath ? (
                <Link href={item.relativepath} title={item.name}>
                  {item.name}
                </Link>
              ) : (
                <span>{item.name}</span>
              )}
            </div>,
            item.pe_ratio === 0 ? 'NA' : item.pe_ratio ?? 'NA',
            item.pb_ratio === 0 ? 'NA' : item.pb_ratio ?? 'NA',
            item.peg_ratio === 0 ? 'NA' : item.peg_ratio ?? 'NA',
            item.eps === 0 ? 'NA' : item.eps ?? 'NA',
            item.roe === 0 ? 'NA' : item.roe ?? 'NA',
            item.roce === 0 ? 'NA' : item.roce ?? 'NA',
            item.dividend_yield ? `${item.dividend_yield}%` : 'NA',
          ],
          url: item.relativepath ?? '',
          backgroundColor:
            item.ind_key === ind_key ? 'rgb(232,244,253)' : 'white',
          name: item.name,
        }
      })
    }
  }

  const [selected, setSelected] = useState('overview')
  const tableData = {
    tableHeading: getTableHeading(selected),
    tableContent: getTableContent(selected),
    innerBorder: false,
  }

  const tabs = [
    {
      name: 'Overview',
      id: 'overview',
    },
    {
      name: 'PE',
      id: 'pe',
    },
    {
      name: 'Returns %',
      id: 'returns',
    },
    {
      name: 'Revenue',
      id: 'revenue',
    },
    {
      name: 'Profit Margin',
      id: 'profitmargin',
    },
    {
      name: 'Ratios',
      id: 'ratios',
    },
  ]

  return (
    <div>
      {heading && (
        <InStockL2Heading
          heading={heading}
          subheading={`Compare market cap, revenue, PE, and other key metrics of ${short_name} with its industry peers.`}
        />
      )}
      <div className="no-scrollbar my-5 flex w-full items-center gap-2 overflow-scroll md:gap-3">
        {tabs.map(item => {
          return (
            <button
              className={classNames(
                'border-1 border-brand-blue-azureRadiance min-w-fit rounded-full px-4 py-1 text-sm font-normal transition-colors',
                {
                  ' bg-brand-blue-azureRadiance text-brand-white':
                    selected === item.id,
                  'bg-brand-white text-brand-blue-azureRadiance':
                    selected !== item.id,
                },
              )}
              onClick={() => setSelected(item.id)}
              key={item.id}
            >
              {item.name}
            </button>
          )
        })}
      </div>
      <ShareHoldingTable
        table={{ ...tableData, withoutOuterBorder: true }}
        sliceLength={5}
      />
    </div>
  )
}
